import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const QueryBuilder = ({ onSaveQuery, onLoadQuery, savedQueries }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [queryName, setQueryName] = useState('');
  const [queryDescription, setQueryDescription] = useState('');
  const [conditions, setConditions] = useState([
    { field: 'close', operator: 'gte', value: '', logic: 'AND' }
  ]);

  const fieldOptions = [
    { value: 'date', label: 'Date' },
    { value: 'contract', label: 'Contract' },
    { value: 'exchange', label: 'Exchange' },
    { value: 'open', label: 'Open Price' },
    { value: 'high', label: 'High Price' },
    { value: 'low', label: 'Low Price' },
    { value: 'close', label: 'Close Price' },
    { value: 'volume', label: 'Volume' },
    { value: 'openInterest', label: 'Open Interest' },
    { value: 'change', label: 'Change' },
    { value: 'changePercent', label: 'Change %' }
  ];

  const operatorOptions = [
    { value: 'eq', label: 'Equals (=)' },
    { value: 'ne', label: 'Not Equals (≠)' },
    { value: 'gt', label: 'Greater Than (>)' },
    { value: 'gte', label: 'Greater Than or Equal (≥)' },
    { value: 'lt', label: 'Less Than (<)' },
    { value: 'lte', label: 'Less Than or Equal (≤)' },
    { value: 'contains', label: 'Contains' },
    { value: 'startswith', label: 'Starts With' },
    { value: 'endswith', label: 'Ends With' }
  ];

  const logicOptions = [
    { value: 'AND', label: 'AND' },
    { value: 'OR', label: 'OR' }
  ];

  const addCondition = () => {
    setConditions([
      ...conditions,
      { field: 'close', operator: 'gte', value: '', logic: 'AND' }
    ]);
  };

  const removeCondition = (index) => {
    if (conditions?.length > 1) {
      setConditions(conditions?.filter((_, i) => i !== index));
    }
  };

  const updateCondition = (index, field, value) => {
    const updated = [...conditions];
    updated[index][field] = value;
    setConditions(updated);
  };

  const handleSaveQuery = () => {
    if (!queryName?.trim()) return;

    const query = {
      id: Date.now()?.toString(),
      name: queryName,
      description: queryDescription,
      conditions: conditions,
      createdAt: new Date()?.toISOString()
    };

    onSaveQuery(query);
    setQueryName('');
    setQueryDescription('');
  };

  const handleLoadQuery = (query) => {
    setConditions(query?.conditions);
    onLoadQuery(query);
  };

  const generateQueryPreview = () => {
    return conditions?.map((condition, index) => {
      const field = fieldOptions?.find(f => f?.value === condition?.field)?.label || condition?.field;
      const operator = operatorOptions?.find(o => o?.value === condition?.operator)?.label || condition?.operator;
      const logic = index > 0 ? ` ${condition?.logic} ` : '';
      
      return `${logic}${field} ${operator} "${condition?.value}"`;
    })?.join('');
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Code" size={20} />
          Custom Query Builder
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} />
        </Button>
      </div>
      {isExpanded && (
        <div className="p-4 space-y-6">
          {/* Query Conditions */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">Query Conditions</h4>
            
            {conditions?.map((condition, index) => (
              <div key={index} className="flex flex-wrap items-end gap-2 p-3 bg-muted/30 rounded-lg">
                {index > 0 && (
                  <Select
                    options={logicOptions}
                    value={condition?.logic}
                    onChange={(value) => updateCondition(index, 'logic', value)}
                    className="w-20"
                  />
                )}
                
                <Select
                  label="Field"
                  options={fieldOptions}
                  value={condition?.field}
                  onChange={(value) => updateCondition(index, 'field', value)}
                  className="w-32"
                />
                
                <Select
                  label="Operator"
                  options={operatorOptions}
                  value={condition?.operator}
                  onChange={(value) => updateCondition(index, 'operator', value)}
                  className="w-40"
                />
                
                <Input
                  label="Value"
                  type="text"
                  value={condition?.value}
                  onChange={(e) => updateCondition(index, 'value', e?.target?.value)}
                  className="w-32"
                />
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeCondition(index)}
                  disabled={conditions?.length === 1}
                  iconName="Trash2"
                />
              </div>
            ))}
            
            <Button
              variant="outline"
              size="sm"
              onClick={addCondition}
              iconName="Plus"
              iconPosition="left"
            >
              Add Condition
            </Button>
          </div>

          {/* Query Preview */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">Query Preview</h4>
            <div className="p-3 bg-muted/30 rounded-lg font-mono text-sm text-foreground">
              {generateQueryPreview() || 'No conditions defined'}
            </div>
          </div>

          {/* Save Query */}
          <div className="space-y-4 pt-4 border-t border-border">
            <h4 className="text-sm font-medium text-foreground">Save Query</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Query Name"
                type="text"
                placeholder="Enter query name"
                value={queryName}
                onChange={(e) => setQueryName(e?.target?.value)}
              />
              <Input
                label="Description (Optional)"
                type="text"
                placeholder="Enter description"
                value={queryDescription}
                onChange={(e) => setQueryDescription(e?.target?.value)}
              />
            </div>
            
            <Button
              variant="default"
              onClick={handleSaveQuery}
              disabled={!queryName?.trim()}
              iconName="Save"
              iconPosition="left"
            >
              Save Query
            </Button>
          </div>

          {/* Saved Queries */}
          {savedQueries && savedQueries?.length > 0 && (
            <div className="space-y-4 pt-4 border-t border-border">
              <h4 className="text-sm font-medium text-foreground">Saved Queries</h4>
              
              <div className="space-y-2">
                {savedQueries?.map((query) => (
                  <div key={query?.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium text-foreground">{query?.name}</div>
                      {query?.description && (
                        <div className="text-sm text-muted-foreground">{query?.description}</div>
                      )}
                      <div className="text-xs text-muted-foreground mt-1">
                        Created: {new Date(query.createdAt)?.toLocaleDateString()}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleLoadQuery(query)}
                        iconName="Play"
                      >
                        Load
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Trash2"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Templates */}
          <div className="space-y-4 pt-4 border-t border-border">
            <h4 className="text-sm font-medium text-foreground">Quick Templates</h4>
            
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConditions([
                  { field: 'close', operator: 'gte', value: '50', logic: 'AND' },
                  { field: 'volume', operator: 'gte', value: '1000', logic: 'AND' }
                ])}
              >
                High Price & Volume
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConditions([
                  { field: 'changePercent', operator: 'gte', value: '5', logic: 'OR' },
                  { field: 'changePercent', operator: 'lte', value: '-5', logic: 'OR' }
                ])}
              >
                High Volatility Days
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConditions([
                  { field: 'contract', operator: 'contains', value: 'EUA', logic: 'AND' },
                  { field: 'exchange', operator: 'eq', value: 'ICE', logic: 'AND' }
                ])}
              >
                EUA on ICE
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueryBuilder;